import { useState, useCallback, useRef } from 'react';

/**
 * A hook that uses the native ResizeObserver API to track element dimensions.
 * Uses a callback ref so it correctly handles elements that mount/unmount
 * or when the ref moves between different DOM elements.
 */
export function useResizeObserver(): {
    ref: ( node: HTMLElement | null ) => void;
    width: number;
    height: number;
} {
    const [ width, setWidth ] = useState( 0 );
    const [ height, setHeight ] = useState( 0 );
    const observerRef = useRef<ResizeObserver | null>( null );
    const nodeRef = useRef<HTMLElement | null>( null );

    const ref = useCallback( ( node: HTMLElement | null ) => {
        // Disconnect previous observer
        if ( observerRef.current ) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        nodeRef.current = node;

        if ( node ) {
            const observer = new ResizeObserver( ( entries ) => {
                for ( const entry of entries ) {
                    const { width: w, height: h } = entry.contentRect;
                    setWidth( w );
                    setHeight( h );
                }
            } );

            observer.observe( node );
            observerRef.current = observer;
        }
    }, [] );

    return { ref, width, height };
}
